import React from 'react';
import clsx from 'clsx';
import { ArrowUturnLeftIcon, PencilIcon } from '@heroicons/react/16/solid';

import { messageOfFailure } from '@oysterjs/core/graphql/errors';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { Combobox } from '@oysterjs/ui/combobox';

import {
  AmsFieldComparison,
  AmsItemDisplay,
  AmsSectionDisplay,
  AmsVerdictOutcome,
  type AmsFieldVerdictFieldsFragment,
  AmsRefusalKind,
  type AmsRefusedChange,
  useApplyAmsReconciliationMutation
} from '@oysterjs/ais/api/graphql';
import { CardFrame, Chevron } from './card-frame';
import { findAmsCodeOption, type CodeOption } from './coded-value';
import {
  flattenFields,
  groupVerdicts,
  startsOpen,
  type ReviewLevel,
  type ReviewNode
} from './group-verdicts';
import { destinationName } from './destinations';
import { InlineEditValue } from './inline-edit';
import type { DestinationFindings } from './pipeline';

/** What one destination's apply call came back with, for the receipt. */
export type DestinationOutcome =
  | { targetId: string; label: string; status: 'written'; written: number }
  // Epic takes each record in its own call, so some landed and the rest are listed.
  | {
      targetId: string;
      label: string;
      status: 'partial';
      written: number;
      refused: RefusedRecord[];
    }
  // The AMS answered and declined, so nothing reached this record.
  | { targetId: string; label: string; status: 'refused'; message: string }
  // The call never came back, so the write may or may not have landed.
  | { targetId: string; label: string; status: 'unconfirmed' };

/**
 * One record whose approved changes did not land, the fields approved on it, how they
 * failed, and why: Epic's reason when it refused them, else Cara's.
 */
export type RefusedRecord = {
  /** Tells apart two records whose subject, kind and message read alike. */
  key: string;
  subject: string;
  fields: string[];
  kind: AmsRefusalKind;
  message: string;
};

/** The reviewer's choices, carried across a retry so they are not made twice. */
export type ReviewDraft = {
  choices: Record<string, boolean>;
  edits: Record<string, string>;
  opened: Record<string, boolean>;
};

const REFUSED = "The AMS wouldn't accept these changes. Nothing was applied.";

// The keys of the items a verdict's item sits under, outermost first: every item level
// of its placement but its own.
const parentKeysOf = (verdict: AmsFieldVerdictFieldsFragment) => {
  const items = verdict.placement.filter((level) => level.display === AmsSectionDisplay.Item);
  return (verdict.itemKey ? items.slice(0, -1) : items).map((level) => level.key);
};

// The record a verdict sits on, named by its placement down to the last record level
// rather than by its index, so a re-run cannot move an edit onto another field. The
// lists and items above keep two premises' subjects of one code apart; a block or
// heading below is left off, so its fields stay on the record they belong to.
const recordIdentity = (verdict: AmsFieldVerdictFieldsFragment) => {
  const levels = verdict.placement;
  let end = 0;
  levels.forEach((level, index) => {
    if (level.record) {
      end = index + 1;
    }
  });
  return levels
    .slice(0, end)
    .map((level) => `${level.display}:${level.key}`)
    .join('/');
};

const rowKey = (destinationId: string, verdict: AmsFieldVerdictFieldsFragment) =>
  `${destinationId}:${recordIdentity(verdict)}:${verdict.fieldPath}`;

/**
 * Match and Keep are listed so the reviewer sees what was checked, but neither is a
 * change. Add covers an empty field and a wholly new record alike.
 */
const OUTCOME: Record<
  AmsVerdictOutcome,
  { label: string; color: 'amber' | 'blue' | 'green' | 'zinc' }
> = {
  [AmsVerdictOutcome.Agrees]: { label: 'Match', color: 'green' },
  [AmsVerdictOutcome.Differs]: { label: 'Update', color: 'amber' },
  [AmsVerdictOutcome.AbsentFromAms]: { label: 'Add', color: 'blue' },
  [AmsVerdictOutcome.OnlyInAms]: { label: 'Keep', color: 'zinc' }
};

// Whether two values say the same thing the pass already decided they do. The
// comparison comes from the field: guessing it from the text would read a ZIP
// "02134" as the number 2134.
export function sameValue(
  a: string,
  b: string,
  options?: readonly CodeOption[],
  comparison?: AmsFieldComparison
): boolean {
  if (options) {
    const codeOf = (value: string) => findAmsCodeOption(options, value)?.code ?? value;
    return codeOf(a) === codeOf(b);
  }
  switch (comparison) {
    case AmsFieldComparison.Currency: {
      const [left, right] = [currencyToFloat(a), currencyToFloat(b)];
      if (left !== undefined && right !== undefined) {
        return left === right;
      }
      break;
    }
    case AmsFieldComparison.Phone: {
      const [left, right] = [phoneDigits(a), phoneDigits(b)];
      if (left.length >= 7 && left === right) {
        return true;
      }
      break;
    }
    case AmsFieldComparison.Date: {
      const [left, right] = [parseDocumentDate(a), parseDocumentDate(b)];
      if (left !== undefined && right !== undefined) {
        return left === right;
      }
      break;
    }
  }
  return normalizedText(a) === normalizedText(b);
}

const normalizedText = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

// currencyToFloat parses "$1,000,000", "1000000.00" and "$1M" to the same number.
// A K, M or MM suffix scales by a thousand or a million. A value that does not
// parse is left to text comparison.
function currencyToFloat(value: string): number | undefined {
  let cleaned = value
    .trim()
    .replace(/[$,\s]/g, '')
    .toUpperCase();
  let multiplier = 1;
  if (cleaned.endsWith('MM')) {
    cleaned = cleaned.slice(0, -2);
    multiplier = 1e6;
  } else if (cleaned.endsWith('M')) {
    cleaned = cleaned.slice(0, -1);
    multiplier = 1e6;
  } else if (cleaned.endsWith('K')) {
    cleaned = cleaned.slice(0, -1);
    multiplier = 1e3;
  }
  if (cleaned === '' || Number.isNaN(Number(cleaned))) {
    return undefined;
  }
  return Number(cleaned) * multiplier;
}

// phoneDigits keeps a number's digits, dropping a leading US country code.
function phoneDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

const MONTHS: Record<string, number> = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12
};

const MONTH_NAME = Object.keys(MONTHS).join('|');

// parseDocumentDate reads a date as Epic or a document writes it and keeps the
// calendar day, so Epic's midnight UTC equals the same day printed without one.
// A two-digit year at or after 69 is 19xx.
function parseDocumentDate(value: string): string | undefined {
  const s = value.trim();
  const iso =
    /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2}))?$/.exec(s);
  if (iso) {
    return calendarDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(s);
  if (slash) {
    const year = slash[3].length === 2 ? expandYear(Number(slash[3])) : Number(slash[3]);
    return calendarDay(year, Number(slash[1]), Number(slash[2]));
  }
  const monthFirst = new RegExp(`^(${MONTH_NAME}) (\\d{1,2}), (\\d{4})$`).exec(s);
  if (monthFirst) {
    return calendarDay(Number(monthFirst[3]), MONTHS[monthFirst[1]], Number(monthFirst[2]));
  }
  const dayFirst = new RegExp(`^(\\d{1,2}) (${MONTH_NAME}) (\\d{4})$`).exec(s);
  if (dayFirst) {
    return calendarDay(Number(dayFirst[3]), MONTHS[dayFirst[2]], Number(dayFirst[1]));
  }
  return undefined;
}

const expandYear = (year: number) => (year >= 69 ? 1900 + year : 2000 + year);

const calendarDay = (year: number, month: number, day: number): string | undefined => {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return undefined;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
};

// What a row will do as the reviewer has left it. An edited value is judged against
// Epic's afresh, so a match edited away from Epic's value becomes an update and an
// update edited back to it becomes a match.
export function shownOutcome(
  verdict: AmsFieldVerdictFieldsFragment,
  edited: string | undefined,
  options?: readonly CodeOption[]
): AmsVerdictOutcome {
  if (edited === undefined) {
    return verdict.outcome;
  }
  const current = verdict.currentValue ?? '';
  if (current === '') {
    return edited === '' ? verdict.outcome : AmsVerdictOutcome.AbsentFromAms;
  }
  return sameValue(edited, current, options, verdict.comparison)
    ? AmsVerdictOutcome.Agrees
    : AmsVerdictOutcome.Differs;
}

const isKeep = (verdict: AmsFieldVerdictFieldsFragment) =>
  verdict.outcome === AmsVerdictOutcome.OnlyInAms;

const isUnchanged = (verdict: AmsFieldVerdictFieldsFragment) =>
  verdict.outcome === AmsVerdictOutcome.Agrees || isKeep(verdict);

const LIST_HEADING =
  'px-3.5 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400';

const isReadOnly = (verdict: AmsFieldVerdictFieldsFragment) => verdict.readOnlyReason != null;

const changedVerdicts = (findings: DestinationFindings) =>
  findings.verdicts.filter((verdict) => !isUnchanged(verdict));

export function ReviewChanges(props: {
  accountName: string;
  findings: DestinationFindings[];
  /** A previous apply's outcomes: destinations already written are not sent again. */
  outcomes?: DestinationOutcome[];
  draft?: ReviewDraft;
  onBack: () => void;
  /** So the modal can refuse to close while the writes are still in flight. */
  onApplyingChange?: (applying: boolean) => void;
  onApplied: (outcomes: DestinationOutcome[], draft: ReviewDraft) => void;
}) {
  // Only what the reviewer ticked or unticked; every other row takes its default.
  const [choices, setChoices] = React.useState<Record<string, boolean>>(props.draft?.choices ?? {});
  const [edits, setEdits] = React.useState<Record<string, string>>(props.draft?.edits ?? {});
  const [editing, setEditing] = React.useState<string | null>(null);
  const [applyError, setApplyError] = React.useState<string | null>(null);
  const [apply, { loading: applying }] = useApplyAmsReconciliationMutation();

  React.useEffect(() => {
    props.onApplyingChange?.(applying);
    return () => props.onApplyingChange?.(false);
  }, [applying, props.onApplyingChange]);

  const groups = props.findings
    .map((findings) => ({
      findings,
      changes: changedVerdicts(findings),
      matched: findings.verdicts.filter((verdict) => verdict.outcome === AmsVerdictOutcome.Agrees)
        .length,
      kept: findings.verdicts.filter(isKeep).length
    }))
    .filter((group) => group.findings.verdicts.length > 0 || group.findings.failure);

  const priorOutcome = new Map(
    (props.outcomes ?? []).map((outcome) => [outcome.targetId, outcome] as const)
  );
  const sendable = (target: DestinationFindings['target']) =>
    target.writable && priorOutcome.get(target.id)?.status !== 'written';
  const totalChanges = groups.reduce((sum, group) => sum + group.changes.length, 0);
  const totalMatched = groups.reduce((sum, group) => sum + group.matched, 0);
  const totalKept = groups.reduce((sum, group) => sum + group.kept, 0);

  const listById = (findings: DestinationFindings, id?: string | null) =>
    id ? findings.codeLists?.find((list) => list.lookupType === id)?.options : undefined;

  // A state's list when its address's country is written as countryValue. A blank or
  // unsupported country falls back to the first supported country's list, as the server
  // does.
  const listForCountryValue = (
    findings: DestinationFindings,
    state: AmsFieldVerdictFieldsFragment,
    country: AmsFieldVerdictFieldsFragment,
    countryValue: string
  ) => {
    const countryOptions = listById(findings, country.optionsId);
    const code =
      (countryOptions && findAmsCodeOption(countryOptions, countryValue)?.code) ?? countryValue;
    const choice =
      state.optionsByValue.find((option) => option.value === code) ?? state.optionsByValue[0];
    return listById(findings, choice?.optionsId ?? state.optionsId);
  };

  // A state's list follows its address's country as it will be written: the
  // reviewer's edit or the document's value when that row is included, else Epic's.
  const verdictOptions = (
    findings: DestinationFindings,
    verdict: AmsFieldVerdictFieldsFragment
  ): readonly CodeOption[] | undefined => {
    const country =
      verdict.optionsDependOn &&
      findings.verdicts.find(
        (other) =>
          recordIdentity(other) === recordIdentity(verdict) &&
          other.fieldPath === verdict.optionsDependOn
      );
    if (!country) {
      return listById(findings, verdict.optionsId);
    }
    const key = rowKey(findings.target.id, country);
    const value = isChosen(key, country, listById(findings, country.optionsId))
      ? valueFor(key, country)
      : (country.currentValue ?? '');
    return listForCountryValue(findings, verdict, country, value);
  };

  const valueFor = (key: string, verdict: AmsFieldVerdictFieldsFragment) =>
    edits[key] ?? verdict.proposedValue ?? '';

  // A coded value the AMS does not list would be refused, so it waits for a pick.
  const isValueUnlisted = (
    key: string,
    verdict: AmsFieldVerdictFieldsFragment,
    options?: readonly CodeOption[]
  ) =>
    !!options &&
    valueFor(key, verdict) !== '' &&
    !findAmsCodeOption(options, valueFor(key, verdict));

  // A change is included until unticked. A match joins once edited or ticked, since
  // as it stands it would resend Epic's own value. A keep has no document value, so it
  // joins only once one is typed; an empty write would clear the field in Epic. An edit
  // back to Epic's own value leaves the row out until ticked, as it changes nothing.
  const isChosen = (
    key: string,
    verdict: AmsFieldVerdictFieldsFragment,
    options?: readonly CodeOption[]
  ) => {
    if ((isKeep(verdict) && !edits[key]) || isValueUnlisted(key, verdict, options)) {
      return false;
    }
    if (shownOutcome(verdict, edits[key], options) === AmsVerdictOutcome.Agrees) {
      return choices[key] ?? false;
    }
    return choices[key] ?? true;
  };

  // The states that would sit beside this country, as written or as Epic keeps them,
  // that its list lacks. The country waits for them, or Epic would hold a Canadian
  // address in Massachusetts.
  const statesOffList = (findings: DestinationFindings, country: AmsFieldVerdictFieldsFragment) => {
    const countryValue = valueFor(rowKey(findings.target.id, country), country);
    return findings.verdicts.filter((state) => {
      if (
        state.optionsDependOn !== country.fieldPath ||
        recordIdentity(state) !== recordIdentity(country)
      ) {
        return false;
      }
      const key = rowKey(findings.target.id, state);
      const options = listForCountryValue(findings, state, country, countryValue);
      const value =
        !isReadOnly(state) && isChosen(key, state, options)
          ? valueFor(key, state)
          : (state.currentValue ?? '');
      return !!options && value !== '' && !findAmsCodeOption(options, value);
    });
  };

  const isChangeIncluded = (
    findings: DestinationFindings,
    key: string,
    verdict: AmsFieldVerdictFieldsFragment,
    options?: readonly CodeOption[]
  ) => isChosen(key, verdict, options) && statesOffList(findings, verdict).length === 0;

  // Only a writable destination's rows can be included: the rest are here to be read.
  const writableRows = groups
    .filter((group) => sendable(group.findings.target))
    .flatMap((group) =>
      group.findings.verdicts
        .filter((verdict) => !isReadOnly(verdict))
        .map((verdict) => ({
          findings: group.findings,
          key: rowKey(group.findings.target.id, verdict),
          verdict,
          options: verdictOptions(group.findings, verdict)
        }))
    );
  const includedRows = writableRows.filter(({ findings, key, verdict, options }) =>
    isChangeIncluded(findings, key, verdict, options)
  );
  const hasEdits = Object.keys(edits).length > 0;

  // Open state is kept only for what the reviewer toggled; everything else takes its
  // default, so a destination is open and a block opens when it holds a change.
  const [opened, setOpened] = React.useState<Record<string, boolean>>(props.draft?.opened ?? {});
  const isOpen = (key: string, byDefault: boolean) => opened[key] ?? byDefault;
  const toggleOpen = (key: string, byDefault: boolean) =>
    setOpened((current) => ({ ...current, [key]: !(current[key] ?? byDefault) }));

  const fieldRow = (
    findings: DestinationFindings,
    verdict: AmsFieldVerdictFieldsFragment,
    showReadOnlyReason: boolean
  ) => {
    const key = rowKey(findings.target.id, verdict);
    const options = verdictOptions(findings, verdict);
    const [heldBy] = isChosen(key, verdict, options) ? statesOffList(findings, verdict) : [];
    return (
      <ChangeRow
        key={key}
        verdict={verdict}
        options={options}
        showReadOnlyReason={showReadOnlyReason}
        heldReason={heldBy && "Held until its state is on this country's list."}
        writable={sendable(findings.target) && !isReadOnly(verdict)}
        included={!isReadOnly(verdict) && isChangeIncluded(findings, key, verdict, options)}
        edited={edits[key]}
        editing={editing === key}
        onToggle={() => {
          if (heldBy) {
            setEditing(rowKey(findings.target.id, heldBy));
          } else if ((isKeep(verdict) && !edits[key]) || isValueUnlisted(key, verdict, options)) {
            setEditing(key);
          } else {
            setChoices((current) => ({
              ...current,
              [key]: !isChangeIncluded(findings, key, verdict, options)
            }));
          }
        }}
        onEdit={() => setEditing(editing === key ? null : key)}
        onChange={(value) => setEdits((current) => ({ ...current, [key]: value }))}
        onReset={() =>
          setEdits((current) => {
            const next = { ...current };
            delete next[key];
            return next;
          })
        }
      />
    );
  };

  // Each level draws as the server placed it: a heading over what it holds, a list's
  // heading over its items, an item or a record as a card, and a block, or an item of a
  // list that draws its items as blocks, as a card inside the card that holds it.
  // `top` marks the destination's own level, where a card is a record.
  const renderNodes = (
    findings: DestinationFindings,
    nodes: readonly ReviewNode[],
    showReadOnlyReason = true,
    within?: string,
    top = false
  ): React.ReactNode =>
    nodes.map((node) => {
      if (node.kind === 'field') {
        return fieldRow(findings, node.verdict, showReadOnlyReason);
      }
      const key = `${findings.target.id}:${node.key}`;
      switch (node.display) {
        case AmsSectionDisplay.Heading:
          return (
            <div key={key}>
              <div className={LIST_HEADING}>{node.label}</div>
              {renderNodes(findings, node.children, showReadOnlyReason, within)}
            </div>
          );
        case AmsSectionDisplay.CardList:
          return node.itemDisplay === AmsItemDisplay.Block ? (
            <React.Fragment key={key}>
              {node.children.map((item) =>
                item.kind === 'level'
                  ? renderBlock(findings, item, showReadOnlyReason, within)
                  : null
              )}
            </React.Fragment>
          ) : (
            <div key={key} className={clsx(!node.label && 'pt-2.5')}>
              {node.label && <div className={LIST_HEADING}>{node.label}</div>}
              {node.children.map((item) =>
                item.kind === 'level' ? renderCard(findings, item, within) : null
              )}
            </div>
          );
        case AmsSectionDisplay.Card:
          // A record's card with no heading above it still needs room to clear the row
          // before it.
          return top ? (
            <div key={key} className="pt-2.5">
              {renderCard(findings, node)}
            </div>
          ) : (
            renderBlock(findings, node, showReadOnlyReason, within)
          );
        default:
          return renderCard(findings, node, within);
      }
    });

  // A block of fields, such as an address, inside the card that holds it.
  const renderBlock = (
    findings: DestinationFindings,
    node: ReviewLevel,
    showReadOnlyReason: boolean,
    within?: string
  ) => {
    const key = `${findings.target.id}:${node.key}`;
    const byDefault = startsOpen(flattenFields(node.children));
    return (
      <CardFrame
        key={key}
        className="mt-2.5"
        title={node.label}
        within={within}
        open={isOpen(key, byDefault)}
        onToggle={() => toggleOpen(key, byDefault)}
        notes={[node.readOnlyReason]}
      >
        {renderNodes(
          findings,
          node.children,
          showReadOnlyReason && !node.readOnlyReason,
          within ? `${node.label} in ${within}` : node.label
        )}
      </CardFrame>
    );
  };

  // A list item or a record, drawn as its own card.
  const renderCard = (findings: DestinationFindings, node: ReviewLevel, within?: string) => {
    const key = `${findings.target.id}:${node.key}`;
    const byDefault = startsOpen(flattenFields(node.children));
    const name = within ? `${node.label} in ${within}` : node.label;
    return (
      <CardFrame
        key={key}
        title={node.label}
        within={within}
        open={isOpen(key, byDefault)}
        onToggle={() => toggleOpen(key, byDefault)}
        notes={[node.matchedOn && `Matched on ${node.matchedOn}`, node.readOnlyReason]}
      >
        {renderNodes(findings, node.children, !node.readOnlyReason, name)}
      </CardFrame>
    );
  };

  // Every destination is tried even after one fails, since they are separate Epic
  // records, and every outcome is kept so the receipt reports partial failure. A
  // destination written in part counts what it wrote before, and its rows that landed
  // come back unticked, so a retry sends only what did not.
  const onApply = async () => {
    setApplyError(null);
    const outcomes: DestinationOutcome[] = (props.outcomes ?? []).filter(
      (outcome) => outcome.status === 'written'
    );
    const nextChoices = { ...choices };

    for (const group of groups) {
      const { target } = group.findings;
      if (!sendable(target) || !target.policyId) {
        continue;
      }
      const prior = priorOutcome.get(target.id);
      const writtenBefore = prior?.status === 'partial' ? prior.written : 0;
      const rows = group.findings.verdicts
        .filter((verdict) => !isReadOnly(verdict))
        .map((verdict) => ({
          key: rowKey(target.id, verdict),
          verdict,
          options: verdictOptions(group.findings, verdict)
        }))
        .filter(({ key, verdict, options }) =>
          isChangeIncluded(group.findings, key, verdict, options)
        );
      const approved = rows.map(({ key, verdict }) => ({
        itemKey: verdict.itemKey,
        // A change to an item the AMS lacks creates it under these.
        parentKeys: parentKeysOf(verdict),
        fieldPath: verdict.fieldPath,
        value: valueFor(key, verdict)
      }));
      if (approved.length === 0) {
        if (prior?.status === 'partial') {
          outcomes.push(prior);
        }
        continue;
      }

      const label = destinationName(target);
      // A destination that partly landed before keeps what landed, however this retry
      // fails, so the receipt still counts it.
      const failed = (kind: AmsRefusalKind, message: string): DestinationOutcome => {
        if (prior?.status === 'partial') {
          const unwritten = rows.map(({ verdict }) => ({
            itemKey: verdict.itemKey,
            parentKeys: parentKeysOf(verdict),
            fieldPath: verdict.fieldPath,
            kind,
            message
          }));
          return {
            targetId: target.id,
            label,
            status: 'partial',
            written: prior.written,
            refused: refusedRecords(group.findings.verdicts, unwritten)
          };
        }
        return kind === AmsRefusalKind.Unconfirmed
          ? { targetId: target.id, label, status: 'unconfirmed' }
          : { targetId: target.id, label, status: 'refused', message };
      };
      try {
        const result = await apply({
          variables: {
            input: { policyId: target.policyId, lineOfBusiness: target.lineOfBusiness, approved }
          }
        });

        // The client sets errorPolicy "all", so a refused write resolves rather than
        // throwing.
        const failure = messageOfFailure(result, REFUSED);
        if (failure || !result.data) {
          outcomes.push(failed(AmsRefusalKind.NotWritten, failure ?? REFUSED));
          continue;
        }
        const { refused } = result.data.applyAmsReconciliation;
        const written = writtenBefore + result.data.applyAmsReconciliation.written;
        if (refused.length > 0) {
          for (const { key, verdict } of rows) {
            const landed = !refused.some(
              (change) =>
                (change.itemKey ?? '') === (verdict.itemKey ?? '') &&
                change.fieldPath === verdict.fieldPath
            );
            if (landed) {
              nextChoices[key] = false;
            }
          }
        }
        outcomes.push(
          refused.length > 0
            ? {
                targetId: target.id,
                label,
                status: 'partial',
                written,
                refused: refusedRecords(group.findings.verdicts, refused)
              }
            : { targetId: target.id, label, status: 'written', written }
        );
      } catch {
        outcomes.push(failed(AmsRefusalKind.Unconfirmed, "Applied Epic didn't answer"));
      }
    }

    if (outcomes.length === 0) {
      return;
    }
    // Every destination refused leaves the AMS untouched, so there is nothing to
    // receipt: staying here lets the reviewer adjust and try again.
    const refusal = outcomes.find((outcome) => outcome.status === 'refused');
    if (refusal && outcomes.every((outcome) => outcome.status === 'refused')) {
      setApplyError(refusal.message);
      return;
    }
    props.onApplied(outcomes, { choices: nextChoices, edits, opened });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex items-center gap-3">
          <p className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">
            Review changes
          </p>
          <span
            aria-disabled="true"
            className="ml-auto flex shrink-0 cursor-not-allowed items-center gap-1.5 whitespace-nowrap rounded-lg border border-neutral-950/10 bg-white px-2.5 py-1 text-sm font-semibold text-neutral-950 opacity-50 shadow-sm dark:border-white/10 dark:bg-neutral-800 dark:text-white"
          >
            <svg
              aria-hidden="true"
              className="size-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
              <path d="M12 7v5l3 2" />
            </svg>
            Value history
          </span>
        </div>
        <p className="m-0 text-xs text-neutral-500 dark:text-neutral-400">
          {totalChanges} {totalChanges === 1 ? 'finding' : 'findings'}
          {totalMatched > 0 && ` · ${totalMatched} matching`}
          {totalKept > 0 && ` · ${totalKept} only in AMS`} across {groups.length}{' '}
          {groups.length === 1 ? 'destination' : 'destinations'}
          {writableRows.length > 0
            ? '. Every writable value is editable before it is written.'
            : '.'}
        </p>
      </div>

      {applyError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {applyError}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-500 dark:border-neutral-600 dark:text-neutral-400">
          No differences found on any destination.
        </div>
      ) : (
        // Capped against the viewport so a long list scrolls inside the modal. The gutter
        // is reserved so rows do not shift when the scrollbar appears.
        <div className="flex max-h-[min(60vh,32rem)] flex-col gap-2.5 overflow-y-auto [scrollbar-gutter:stable]">
          {groups.map((group) => {
            const { target, failure } = group.findings;
            // A failed pass has no rows, so there is nothing to open.
            const open = !failure && isOpen(target.id, true);
            const subtitle = [target.detail, groupNote(group.findings, priorOutcome.get(target.id))]
              .filter(Boolean)
              .join(' · ');
            return (
              // overflow-clip rounds the corners without becoming a scroll container,
              // which would stop the header below from sticking.
              <section
                key={target.id}
                className="shrink-0 overflow-clip rounded-[10px] border border-neutral-950/10 dark:border-white/10"
              >
                {/* The destination and its column names stay in view while its rows scroll. */}
                <div className="sticky top-0 z-10">
                  <div
                    className={clsx(
                      'flex items-center gap-3 bg-neutral-50 px-3.5 py-3 dark:bg-neutral-900',
                      open && 'border-b border-neutral-950/[.08] dark:border-white/10'
                    )}
                  >
                    {failure ? (
                      <span aria-hidden="true" className="-m-1 size-6 flex-none" />
                    ) : (
                      <Chevron
                        open={open}
                        name={destinationName(target)}
                        onClick={() => toggleOpen(target.id, true)}
                      />
                    )}
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm font-semibold text-neutral-950 dark:text-white">
                        {destinationName(target)}
                      </span>
                      {subtitle && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          {subtitle}
                        </span>
                      )}
                    </span>
                  </div>

                  {open && (
                    <div className="grid grid-cols-[20px_1.5fr_1fr_1.2fr_84px] gap-3 border-b border-neutral-950/5 bg-white px-3 py-2 text-xs font-medium text-neutral-500 dark:border-white/5 dark:bg-neutral-900 dark:text-neutral-400">
                      <span />
                      <span>Field</span>
                      <span>In AMS</span>
                      <span>Write this value</span>
                      <span>Outcome</span>
                    </div>
                  )}
                </div>

                {open && (
                  <div className="pb-1.5">
                    {renderNodes(
                      group.findings,
                      groupVerdicts(group.findings.verdicts),
                      true,
                      undefined,
                      true
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-neutral-200 pt-3 dark:border-neutral-700">
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          {writableRows.length === 0
            ? totalChanges === 0 && !groups.some((group) => group.findings.failure)
              ? 'Everything the document states matches the AMS.'
              : 'Nothing here can be written back yet.'
            : `${includedRows.length} of ${writableRows.length} fields included`}
        </div>
        <div className="flex items-center gap-2">
          {hasEdits && (
            <button
              type="button"
              onClick={() => setEdits({})}
              className="text-xs font-medium text-sky-700 hover:underline dark:text-sky-300"
            >
              Revert all edits
            </button>
          )}
          <Button type="button" color="white" disabled={applying} onClick={props.onBack}>
            Back
          </Button>
          <Button
            type="button"
            color="sky"
            disabled={includedRows.length === 0 || applying}
            onClick={() => void onApply()}
          >
            {applying ? 'Applying…' : `Apply ${includedRows.length} changes`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// A new item's key is only its place under its parent, so two premises' first subjects
// share one; an item is told apart by its parents' keys with its own.
function sameItem(
  verdict: AmsFieldVerdictFieldsFragment,
  parentKeys: readonly string[],
  itemKey: string | null | undefined
): boolean {
  return (
    (verdict.itemKey ?? '') === (itemKey ?? '') &&
    parentKeysOf(verdict).join('\u0000') === parentKeys.join('\u0000')
  );
}

// Groups refused changes by the record they sit on, since Epic refuses a record whole,
// and names each by the records it sits in so two records with one label also read
// apart.
function refusedRecords(
  verdicts: readonly AmsFieldVerdictFieldsFragment[],
  refused: readonly AmsRefusedChange[]
): RefusedRecord[] {
  const records = new Map<string, RefusedRecord>();
  for (const change of refused) {
    const verdict = verdicts.find(
      (candidate) =>
        sameItem(candidate, change.parentKeys, change.itemKey) &&
        candidate.fieldPath === change.fieldPath
    );
    const recordLevels = (verdict?.placement ?? []).filter((level) => level.record);
    const subject =
      recordLevels.map((level) => level.label).join(' · ') || change.itemKey || 'This line';
    const identity = verdict
      ? recordIdentity(verdict)
      : `${AmsSectionDisplay.Item}:${change.itemKey ?? ''}`;
    const key = `${identity}\u0000${change.kind}\u0000${change.message}`;
    const record = records.get(key) ?? {
      key,
      subject,
      fields: [],
      kind: change.kind,
      message: change.message
    };
    record.fields.push(verdict?.fieldName || change.fieldPath);
    records.set(key, record);
  }
  return [...records.values()];
}

function groupNote(
  findings: DestinationFindings,
  prior: DestinationOutcome | undefined
): string | undefined {
  const { target } = findings;
  if (findings.failure) {
    return findings.failure;
  }
  if (!target.writable) {
    return "Review only. Cara doesn't write this line of business to Epic.";
  }
  switch (prior?.status) {
    case 'written':
      return 'Written to AMS, not sent again';
    case 'unconfirmed':
      return 'May already be written. Sending the same values again is safe.';
    case 'refused':
      return 'Not written last time';
    case 'partial':
      return 'Partly written last time. Sending the same values again is safe.';
    default:
      return undefined;
  }
}

function ChangeRow(props: {
  verdict: AmsFieldVerdictFieldsFragment;
  /** The AMS's options when the field is coded; absent for free text. */
  options?: readonly CodeOption[];
  writable: boolean;
  included: boolean;
  edited?: string;
  editing: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onChange: (value: string) => void;
  onReset: () => void;
  /** False when the card above already gives the reason. */
  showReadOnlyReason: boolean;
  /** Why a change the reviewer chose waits on another row. */
  heldReason?: string;
}) {
  const { verdict } = props;
  // The full label names the checkbox, so two items' City fields stay apart for a
  // screen reader; the row shows the short name, since its card and block say the rest.
  // The path is kept for the tooltip: it is what an agent would look up in Epic.
  const name = verdict.fieldLabel || verdict.fieldPath;
  const shown = verdict.fieldName || name;
  const outcome = OUTCOME[shownOutcome(verdict, props.edited, props.options)];
  // A row that won't be written fades, but stays readable so it can be ticked back.
  const faded = props.writable && !props.included;
  const extracted = verdict.proposedValue ?? '';
  const value = props.edited ?? extracted;
  const resetLabel = extracted ? `Use ${extracted} from document` : "Use the document's value";

  return (
    <div className="grid grid-cols-[20px_1.5fr_1fr_1.2fr_84px] items-center gap-3 border-b border-neutral-950/5 px-3 py-2.5 text-sm last:border-b-0 dark:border-white/5">
      <span className="flex items-center">
        {props.writable && (
          <Checkbox
            color="sky"
            checked={props.included}
            onChange={props.onToggle}
            aria-label={`Include ${name}`}
          />
        )}
      </span>

      <span className={clsx('min-w-0 transition-opacity', faded && 'opacity-50')}>
        <span
          title={verdict.fieldPath}
          className="block truncate font-medium text-neutral-800 dark:text-neutral-200"
        >
          {shown}
        </span>
        {props.showReadOnlyReason && verdict.readOnlyReason && (
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
            {verdict.readOnlyReason}
          </span>
        )}
        {props.heldReason && (
          <span className="block text-xs text-amber-700 dark:text-amber-400">
            {props.heldReason}
          </span>
        )}
      </span>

      {verdict.currentValue == null ? (
        <span
          className={clsx(
            'min-w-0 break-words italic text-neutral-400 transition-opacity dark:text-neutral-500',
            faded && 'opacity-50'
          )}
        >
          Not in AMS
        </span>
      ) : (
        <span
          className={clsx(
            'min-w-0 break-words text-neutral-500 transition-opacity dark:text-neutral-400',
            faded && 'opacity-50'
          )}
        >
          <CodedText options={props.options} value={verdict.currentValue} />
        </span>
      )}

      <span className={clsx('min-w-0 transition-opacity', faded && 'opacity-50')}>
        {props.options ? (
          <CodedValue
            name={name}
            options={props.options}
            value={value}
            writable={props.writable}
            editing={props.editing}
            onEdit={props.onEdit}
            onChange={props.onChange}
            resetAction={
              props.edited !== undefined &&
              props.edited !== extracted && (
                <button
                  type="button"
                  onClick={props.onReset}
                  aria-label={resetLabel}
                  title={resetLabel}
                  className="rounded p-1 text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
                >
                  <ArrowUturnLeftIcon className="size-3.5" />
                </button>
              )
            }
          />
        ) : (
          <InlineEditValue
            label={`Value for ${name}`}
            editLabel={`Edit ${name}`}
            value={value}
            placeholder="—"
            editing={props.editing}
            editable={props.writable}
            onStartEditing={props.onEdit}
            onChange={props.onChange}
            onDoneEditing={props.onEdit}
            valueClassName="font-medium text-neutral-800 dark:text-neutral-200"
            editingAction={
              props.edited !== undefined &&
              props.edited !== extracted && (
                // Beside the value rather than under it, so the rows below never move.
                <button
                  type="button"
                  onClick={props.onReset}
                  aria-label={resetLabel}
                  title={resetLabel}
                  onMouseDown={(event) => event.preventDefault()}
                  className="rounded p-1 text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
                >
                  <ArrowUturnLeftIcon className="size-3.5" />
                </button>
              )
            }
          />
        )}
      </span>

      <span className={clsx('transition-opacity', faded && 'opacity-50')}>
        <Badge color={outcome.color}>{outcome.label}</Badge>
      </span>
    </div>
  );
}

/** A coded value in the AMS's words, with the code it stores beside it. */
function CodedText(props: { options?: readonly CodeOption[]; value: string }) {
  const option = props.options && findAmsCodeOption(props.options, props.value);
  if (!option) {
    return <>{props.value}</>;
  }
  return (
    <>
      {option.label}{' '}
      <span className="text-xs text-neutral-400 dark:text-neutral-500">· {option.code}</span>
    </>
  );
}

/**
 * A coded field's value, picked from the AMS's list rather than typed: the pencil opens
 * a searchable list in the AMS's own order, and a pick, Escape or a click outside
 * closes it.
 */
function CodedValue(props: {
  name: string;
  options: readonly CodeOption[];
  value: string;
  writable: boolean;
  editing: boolean;
  onEdit: () => void;
  onChange: (value: string) => void;
  resetAction: React.ReactNode;
}) {
  const current = findAmsCodeOption(props.options, props.value);

  if (props.editing) {
    return (
      <div className="flex min-w-0 items-center gap-1">
        <div className="min-w-0 flex-1">
          <Combobox
            value={current?.code}
            options={props.options.map((option) => ({
              value: option.code,
              label: option.label,
              description: option.code
            }))}
            placeholder="Search the AMS's options"
            minQueryLength={0}
            maxOptions={props.options.length}
            autoFocus
            onChange={(code) => {
              if (code) {
                props.onChange(code);
              }
            }}
            // The list closes after a pick as well, so this is the one way out of
            // editing; leaving from onChange too would toggle editing back on.
            onClose={props.onEdit}
          />
        </div>
        <span className="grid size-7 flex-none place-items-center">{props.resetAction}</span>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col">
      <div className="flex min-w-0 items-center gap-1">
        <span className="min-w-0 flex-1 truncate font-medium text-neutral-800 dark:text-neutral-200">
          {props.value ? <CodedText options={props.options} value={props.value} /> : '—'}
        </span>
        <span className="grid size-7 flex-none place-items-center">
          {props.writable && (
            <button
              type="button"
              aria-label={`Edit ${props.name}`}
              onClick={props.onEdit}
              className="rounded p-1 text-neutral-500 hover:bg-neutral-950/5 dark:text-neutral-400 dark:hover:bg-white/5"
            >
              <PencilIcon className="size-3.5" />
            </button>
          )}
        </span>
      </div>
      {props.value && !current && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          Not an option in this AMS. Pick one to include it.
        </span>
      )}
    </div>
  );
}
